import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, StatCard } from "@/components/common/DashboardBits";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from "recharts";
import { useLang } from "@/contexts/LanguageContext";

const LINE_DOT = { r: 3 };
const BAR_RADIUS = [4, 4, 0, 0];
const inr = (v) => `₹ ${Number(v).toLocaleString("en-IN")}`;

export default function ReportsPage() {
  const { t } = useLang();
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/reports/earnings").then((r) => setData(r.data)); }, []);

  return (
    <div>
      <PageHeader title={t("reports")} subtitle={t("earnings")} />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <StatCard label={t("grand_total")} value={`₹ ${Number(data?.grand_total || 0).toLocaleString("en-IN")}`} tone="green" testId="report-grand-total" />
        <StatCard label={"Last month"} value={`₹ ${Number(data?.monthly?.slice(-1)?.[0]?.amount || 0).toLocaleString("en-IN")}`} tone="blue" testId="report-last-month" />
        <StatCard label={"Executives paid"} value={data?.by_executive?.length ?? 0} tone="amber" testId="report-execs-paid" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border border-slate-200">
          <CardContent className="p-6">
            <div className="text-xs font-bold uppercase tracking-widest text-slate-500">{t("daily_earnings")}</div>
            <div className="font-heading text-lg font-semibold text-slate-950 mt-1 mb-4">Last 30 days</div>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={data?.daily || []}>
                  <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="date" stroke="#94a3b8" fontSize={11} tickFormatter={(d) => d.slice(5)} />
                  <YAxis stroke="#94a3b8" fontSize={11} />
                  <Tooltip formatter={inr} />
                  <Line type="monotone" dataKey="amount" stroke="#2563eb" strokeWidth={2} dot={LINE_DOT} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="border border-slate-200">
          <CardContent className="p-6">
            <div className="text-xs font-bold uppercase tracking-widest text-slate-500">{t("monthly_earnings")}</div>
            <div className="font-heading text-lg font-semibold text-slate-950 mt-1 mb-4">Last 12 months</div>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data?.monthly || []}>
                  <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="month" stroke="#94a3b8" fontSize={11} />
                  <YAxis stroke="#94a3b8" fontSize={11} />
                  <Tooltip formatter={inr} />
                  <Bar dataKey="amount" fill="#0f172a" radius={BAR_RADIUS} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="border border-slate-200 mt-6">
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader><TableRow><TableHead>Executive</TableHead><TableHead className="text-right">Total Earnings (₹)</TableHead></TableRow></TableHeader>
            <TableBody>
              {(data?.by_executive || []).map((e) => (
                <TableRow key={e.executive_id} data-testid={`earning-row-${e.executive_id}`}>
                  <TableCell className="font-medium">{e.executive_name}</TableCell>
                  <TableCell className="text-right num">₹ {Number(e.total_inr).toLocaleString("en-IN")}</TableCell>
                </TableRow>
              ))}
              {(!data?.by_executive || data.by_executive.length === 0) && (
                <TableRow><TableCell colSpan={2} className="text-center text-slate-500 py-8">No earnings recorded yet.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
